// src/pages/ProjectManagement.js - FIXED BUDGET PASSING
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  Paper,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Divider,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  InputAdornment,
  Collapse,
  alpha,
  useTheme,
  useMediaQuery,
  Pagination,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { DataGrid } from '@mui/x-data-grid';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import SearchIcon from '@mui/icons-material/Search';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import projectService from '../services/projectService';
import { useProjects } from '../context/ProjectContext';
import ProjectFormDialog from '../components/ProjectFormDialog';

const PROJECT_TABLE_PREFS_KEY = 'project-management-table-prefs-v1';

const getStatusChipColor = (status) => {
  switch (status) {
    case 'in_progress': return 'info';
    case 'completed': return 'success';
    case 'on_hold': return 'error';
    case 'planning':
    default: return 'warning';
  }
};

const getStatusLabel = (status) => {
  const labels = {
    'planning': 'Planning',
    'in_progress': 'In Progress',
    'completed': 'Completed',
    'on_hold': 'On Hold',
  };
  return labels[status] || status;
};

const STATUS_OPTIONS = ['', 'planning', 'in_progress', 'completed', 'on_hold'];
const formatCurrency = (amount, currency = 'MXN') => {
  const numericAmount = Number(amount) || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency
  }).format(numericAmount);
};

const getQuoteSummary = (project) => {
  if (project?.latest_quote_number) {
    return {
      label: project.latest_quote_number,
      amount: project.latest_quote_total,
      currency: project.latest_quote_currency || 'MXN',
      hasQuote: true
    };
  }

  if (Number(project?.budget) > 0) {
    return {
      label: 'Legacy budget',
      amount: project.budget,
      currency: 'MXN',
      hasQuote: false
    };
  }

  return {
    label: 'Pending QT',
    amount: null,
    currency: 'MXN',
    hasQuote: false
  };
};

const getBusinessUnitsSummary = (units) => {
  if (!Array.isArray(units) || units.length === 0) return 'No units';
  if (units.length <= 2) return units.join(', ');
  return `${units.slice(0, 2).join(', ')} +${units.length - 2}`;
};

export default function ProjectManagement() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  const { projects, loading: contextLoading, userRole, refreshProjects } = useProjects();

  const [uiPrefs, setUiPrefs] = useState(() => {
    try {
      const raw = localStorage.getItem(PROJECT_TABLE_PREFS_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      return {
        showFilters: parsed?.showFilters ?? false,
        showKpis: parsed?.showKpis ?? false,
        tablePageSize: parsed?.tablePageSize ?? 25,
        columnVisibilityModel: parsed?.columnVisibilityModel ?? {
          company_name: true,
          contact_person: true,
          business_units: true,
          created_at: true
        }
      };
    } catch (error) {
      return {
        showFilters: false,
        showKpis: false,
        tablePageSize: 25,
        columnVisibilityModel: {
          company_name: true,
          contact_person: true,
          business_units: true,
          created_at: true
        }
      };
    }
  });

  const [localLoading, setLocalLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ q: '', status: '' });
  const [page, setPage] = useState(1);

  const [openDialog, setOpenDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [editingProject, setEditingProject] = useState(null);
  const [desktopPaginationModel, setDesktopPaginationModel] = useState({
    page: 0,
    pageSize: uiPrefs.tablePageSize
  });

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    client_id: '',
    status: 'planning',
    intercompany_sale_enabled: false,
    company_name: '',
    contact_phone: '',
    contact_email: '',
    contact_person: '',
    requirements: '',
    business_units: [],
    attachments: [],
  });

  const itemsPerPage = 5;

  useEffect(() => {
    localStorage.setItem(PROJECT_TABLE_PREFS_KEY, JSON.stringify(uiPrefs));
  }, [uiPrefs]);

  const filtered = useMemo(() => {
    return projects.filter((p) => {
      const mq = filters.q
        ? [
          p.name,
          p.company_name,
          p.contact_person,
          p.contact_email,
          p.latest_quote_number
        ].filter(Boolean).join(' ').toLowerCase().includes(filters.q.toLowerCase())
        : true;
      const ms = filters.status ? p.status === filters.status : true;
      return mq && ms;
    });
  }, [projects, filters]);

  const paginatedProjects = useMemo(() => {
    if (!isMobile) return filtered;
    const startIndex = (page - 1) * itemsPerPage;
    return filtered.slice(startIndex, startIndex + itemsPerPage);
  }, [filtered, page, isMobile]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const hasActiveFilters = Boolean(filters.q.trim() || filters.status);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const stats = useMemo(
    () => ({
      total: projects.length,
      inProgress: projects.filter((p) => p.status === 'in_progress').length,
      completed: projects.filter((p) => p.status === 'completed').length,
      planning: projects.filter((p) => p.status === 'planning').length,
    }),
    [projects]
  );

  const updateUiPrefs = useCallback((patch) => {
    setUiPrefs((prev) => ({ ...prev, ...patch }));
  }, []);

  const handleOpenProjectWorkspace = useCallback((project) => {
    navigate(`/projects/${project.id}`);
  }, [navigate]);

  const handleOpenProjectQuotes = useCallback((project) => {
    navigate(`/finance?project_id=${project.id}&tab=quotes`);
  }, [navigate]);

  const toggleColumnVisibility = useCallback((field) => {
    updateUiPrefs({
      columnVisibilityModel: {
        ...uiPrefs.columnVisibilityModel,
        [field]: !(uiPrefs.columnVisibilityModel?.[field] ?? true)
      }
    });
  }, [uiPrefs.columnVisibilityModel, updateUiPrefs]);

  const handleOpenDialog = useCallback((project = null) => {
    if (project) {
      setEditingProject(project);
      // ✅ CORRECCIÓN: Incluir budget al cargar datos para editar
      setFormData({
        name: project.name,
        description: project.description || '',
        client_id: project.client_id || '',
        status: project.status,
        intercompany_sale_enabled: Boolean(project.intercompany_sale_enabled),
        company_name: project.company_name || '',
        contact_phone: project.contact_phone || '',
        contact_email: project.contact_email || '',
        contact_person: project.contact_person || '',
        requirements: project.requirements || '',
        business_units: project.business_units || [],
        attachments: [],
      });
    } else {
      setEditingProject(null);
      setFormData({
        name: '',
        description: '',
        client_id: '',
        status: 'planning',
        intercompany_sale_enabled: false,
        company_name: '',
        contact_phone: '',
        contact_email: '',
        contact_person: '',
        requirements: '',
        business_units: [],
        attachments: [],
      });
    }
    setOpenDialog(true);
  }, []);

  useEffect(() => {
    if (!location.state?.openDialog) return;
    handleOpenDialog();
    window.history.replaceState({}, document.title);
  }, [handleOpenDialog, location]);

  const handleSaveProject = async (localDataOverride = null) => {
    try {
      const dataToSave = localDataOverride || formData;

      if (!dataToSave.name) {
        setError('Project name is required');
        return;
      }

      if (!dataToSave.company_name) {
        setError('Company name is required');
        return;
      }

      if (dataToSave.business_units.length === 0) {
        setError('You must select at least one business unit');
        return;
      }

      setLocalLoading(true);

      const submitData = new FormData();

      Object.keys(dataToSave).forEach((key) => {
        if (key === 'attachments') {
          dataToSave.attachments.forEach((file) => {
            submitData.append('attachments', file);
          });
        } else if (key === 'business_units') {
          submitData.append('business_units', JSON.stringify(dataToSave.business_units));
        } else {
          submitData.append(key, dataToSave[key]);
        }
      });

      if (editingProject) {
        submitData.append('expected_revision', editingProject._edit_revision || '');
        await projectService.updateProject(editingProject.id, submitData);
      } else {
        await projectService.createProject(submitData);
      }

      setError('');
      setOpenDialog(false);

      refreshProjects();

    } catch (err) {
      setError('Error saving project: ' + (err.response?.data?.message || err.message));
    } finally {
      setLocalLoading(false);
    }
  };

  const handleDeleteProject = async (projectId) => {
    try {
      setLocalLoading(true);
      await projectService.deleteProject(projectId);
      setDeleteConfirm(null);

      refreshProjects();

    } catch (err) {
      setError('Error deleting project: ' + err.message);
    } finally {
      setLocalLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Project Name',
        flex: 1,
        minWidth: 180,
        renderCell: (p) => (
          <Tooltip title={p.row.name}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%'
              }}
            >
              {p.row.name}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'company_name',
        headerName: 'Company',
        flex: 1,
        minWidth: 180,
        renderCell: (p) => (
          <Tooltip title={p.row.client_company_name || p.row.company_name || 'No linked client'}>
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%'
              }}
            >
              {p.row.client_company_name || p.row.company_name || 'No linked client'}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'contact_person',
        headerName: 'Contact',
        flex: 1,
        minWidth: 180,
        renderCell: (p) => (
          <Tooltip title={p.row.contact_person || p.row.contact_email || p.row.contact_phone || 'No contact'}>
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%'
              }}
            >
              {p.row.contact_person || p.row.contact_email || p.row.contact_phone || 'No contact'}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'business_units',
        headerName: 'Business Units',
        flex: 1,
        minWidth: 180,
        renderCell: (p) => (
          <Tooltip title={Array.isArray(p.value) && p.value.length ? p.value.join(', ') : 'No units'}>
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%'
              }}
            >
              {getBusinessUnitsSummary(p.value)}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'latest_quote_number',
        headerName: 'Quote',
        width: 150,
        renderCell: (p) => (
          (() => {
            const quoteSummary = getQuoteSummary(p.row);
            return (
              <Chip
                label={quoteSummary.label}
                size="small"
                color={quoteSummary.hasQuote ? 'primary' : 'default'}
                variant={quoteSummary.hasQuote ? 'filled' : 'outlined'}
                sx={{ fontWeight: 700, maxWidth: '100%' }}
              />
            );
          })()
        ),
      },
      {
        field: 'latest_quote_total',
        headerName: 'Budget',
        width: 130,
        headerAlign: 'right',
        align: 'right',
        renderCell: (p) => {
          const quoteSummary = getQuoteSummary(p.row);
          return (
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {quoteSummary.amount !== null
                ? formatCurrency(quoteSummary.amount, quoteSummary.currency)
                : '-'}
            </Typography>
          );
        }
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        headerAlign: 'center',
        align: 'center',
        renderCell: (p) => (
          <Chip
            label={getStatusLabel(p.value)}
            color={getStatusChipColor(p.value)}
            size="small"
            sx={{ fontWeight: 600, minWidth: 110 }}
          />
        ),
      },
      {
        field: 'created_at',
        headerName: 'Created',
        width: 130,
        headerAlign: 'center',
        align: 'center',
        valueGetter: (value) =>
          value ? new Date(value).toLocaleDateString('en-US') : '',
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 190,
        sortable: false,
        disableColumnMenu: true,
        headerAlign: 'center',
        align: 'center',
        renderCell: (p) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="View Details">
              <IconButton
                size="small"
                sx={{ color: 'primary.main', '&:hover': { bgcolor: alpha('#1976d2', 0.1) } }}
                onClick={() => handleOpenProjectWorkspace(p.row)}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Open Quotes">
              <IconButton
                size="small"
                sx={{ color: 'warning.main', '&:hover': { bgcolor: alpha('#ed6c02', 0.1) } }}
                onClick={() => handleOpenProjectQuotes(p.row)}
              >
                <Typography variant="caption" sx={{ fontWeight: 800 }}>
                  QT
                </Typography>
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                sx={{ color: 'info.main', '&:hover': { bgcolor: alpha('#0288d1', 0.1) } }}
                onClick={() => handleOpenDialog(p.row)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                sx={{ color: 'error.main', '&:hover': { bgcolor: alpha('#d32f2f', 0.1) } }}
                onClick={() => setDeleteConfirm(p.row)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [handleOpenDialog, handleOpenProjectQuotes, handleOpenProjectWorkspace]
  );

  const StatCard = ({ title, value, icon: Icon, color }) => (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        transition: 'all 0.3s ease',
        '&:hover': { boxShadow: 3, transform: 'translateY(-4px)', borderColor: color },
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: color }}>
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              bgcolor: alpha(color, 0.1),
              borderRadius: 2,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon sx={{ fontSize: 32, color: color }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const ProjectCard = ({ project, onView, onEdit, onDelete }) => {
    const quoteSummary = getQuoteSummary(project);

    return (
      <Card
        sx={{
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: 4,
            transform: 'translateY(-2px)',
          },
        }}
      >
        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
          <Box sx={{ mb: 1.25, display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.4, overflowWrap: 'anywhere' }}>
                {project.name}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ overflowWrap: 'anywhere' }}>
                {project.client_company_name || project.company_name || 'No Company'}
              </Typography>
            </Box>
            <Chip
              label={getStatusLabel(project.status)}
              color={getStatusChipColor(project.status)}
              size="small"
              sx={{ fontWeight: 700, flexShrink: 0 }}
            />
          </Box>
          <Box sx={{ mb: 1.25 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
              {project.contact_person || 'No contact'} {project.contact_email ? `| ${project.contact_email}` : ''}
            </Typography>
          </Box>

          <Box sx={{ mb: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={quoteSummary.label}
              variant="outlined"
              size="small"
            />
            <Chip
              label={quoteSummary.amount !== null
                ? formatCurrency(quoteSummary.amount, quoteSummary.currency)
                : 'Pending QT'}
              variant="outlined"
              size="small"
            />
            {project.intercompany_sale_enabled && (
              <Chip
                label="LLC -> SA de CV"
                color="secondary"
                size="small"
                sx={{ fontWeight: 700 }}
              />
            )}
            <Chip
              label={new Date(project.created_at).toLocaleDateString('en-US')}
              variant="outlined"
              size="small"
            />
          </Box>

          <Divider sx={{ my: 1 }} />

          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Tooltip title="View Details">
              <IconButton aria-label="View project details" size="small" color="primary" onClick={() => onView(project)} sx={{ width: 40, height: 40 }}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Open Quotes">
              <IconButton aria-label="Open project quotes" size="small" color="warning" onClick={() => handleOpenProjectQuotes(project)} sx={{ width: 40, height: 40 }}>
                <Typography variant="caption" sx={{ fontWeight: 800 }}>QT</Typography>
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton aria-label="Edit project" size="small" color="info" onClick={() => onEdit(project)} sx={{ width: 40, height: 40 }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton aria-label="Delete project" size="small" color="error" onClick={() => onDelete(project)} sx={{ width: 40, height: 40 }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>
    );
  };

  if (contextLoading || localLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.4, sm: 1.7 },
          mb: 1.5,
          borderRadius: 2.5,
          background: 'linear-gradient(135deg, #135cb4 0%, #102f66 100%)',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box sx={{ bgcolor: 'rgba(255,255,255,.16)', borderRadius: 2, p: 1, display: 'flex' }}>
              <BusinessCenterIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.2 }}>
                Projects
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {stats.total} registered projects
                </Typography>
                {userRole && (
                  <Chip
                    label={userRole === 'user' ? 'My Projects' : userRole === 'admin' ? 'All Projects' : 'Management'}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontWeight: 600
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddCircleIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              bgcolor: 'white',
              color: 'primary.main',
              fontWeight: 600,
              px: 2,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.9)', transform: 'translateY(-2px)', boxShadow: 4 },
              transition: 'all 0.3s ease',
            }}
          >
            New Project
          </Button>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{ p: { xs: 1.25, sm: 2 }, mb: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
          <Stack direction="row" spacing={1} useFlexGap sx={{ minWidth: 0, overflowX: { xs: 'auto', sm: 'visible' }, pb: { xs: 0.5, sm: 0 }, '& .MuiChip-root': { flexShrink: 0 } }}>
            <Chip label={`Total ${stats.total}`} color="primary" variant="outlined" />
            <Chip label={`In Progress ${stats.inProgress}`} color="info" variant="outlined" />
            <Chip label={`Completed ${stats.completed}`} color="success" variant="outlined" />
            <Chip label={`Planning ${stats.planning}`} color="warning" variant="outlined" />
          </Stack>
          <Stack direction="row" spacing={1} useFlexGap sx={{ '& .MuiButton-root': { flex: { xs: 1, sm: 'initial' } } }}>
            <Button
              size="small"
              variant={uiPrefs.showFilters ? 'contained' : 'outlined'}
              onClick={() => updateUiPrefs({ showFilters: !uiPrefs.showFilters })}
              endIcon={uiPrefs.showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              Filters
            </Button>
            <Button
              size="small"
              variant={uiPrefs.showKpis ? 'contained' : 'outlined'}
              onClick={() => updateUiPrefs({ showKpis: !uiPrefs.showKpis })}
              endIcon={uiPrefs.showKpis ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              KPIs
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Collapse in={uiPrefs.showFilters}>
        <Paper
          elevation={0}
          sx={{ p: { xs: 1.25, sm: 2 }, mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
        >
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, alignItems: { xs: 'stretch', sm: 'center' } }}>
            <TextField
              size="small"
              placeholder="Search project, company, contact or QT..."
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              fullWidth={isMobile}
              sx={{ flex: 1, minWidth: { xs: 0, sm: 220 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                )
              }}
            />
            <TextField
              size="small"
              select
              label="Status"
              value={filters.status}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
              fullWidth={isMobile}
              sx={{ minWidth: { xs: 0, sm: 180 } }}
            >
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s || 'all'} value={s}>
                  {s ? getStatusLabel(s) : 'All'}
                </MenuItem>
              ))}
            </TextField>
            {hasActiveFilters && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<FilterAltOffIcon />}
                onClick={() => setFilters({ q: '', status: '' })}
                sx={{ minHeight: 40 }}
              >
                Clear filters
              </Button>
            )}
            <Chip
              label={`${filtered.length} result${filtered.length === 1 ? '' : 's'}`}
              color="primary"
              variant="outlined"
            />
          </Box>
        </Paper>
      </Collapse>

      <Collapse in={uiPrefs.showKpis}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Total Projects" value={stats.total} icon={AssignmentIcon} color="#1976d2" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="In Progress" value={stats.inProgress} icon={TrendingUpIcon} color="#0288d1" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Completed" value={stats.completed} icon={CheckCircleIcon} color="#2e7d32" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard title="Planned" value={stats.planning} icon={PendingActionsIcon} color="#ed6c02" />
          </Grid>
        </Grid>
      </Collapse>

      {/* Content */}
      <Paper
        elevation={0}
        sx={{ p: { xs: 1.25, sm: 2, md: 3 }, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
      >
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Projects List
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View, filter and continue working on your active projects faster.
              </Typography>
            </Box>
            {!isMobile && (
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip
                  clickable
                  label={`Company ${uiPrefs.columnVisibilityModel.company_name ? 'On' : 'Off'}`}
                  color={uiPrefs.columnVisibilityModel.company_name ? 'primary' : 'default'}
                  variant={uiPrefs.columnVisibilityModel.company_name ? 'filled' : 'outlined'}
                  onClick={() => toggleColumnVisibility('company_name')}
                />
                <Chip
                  clickable
                  label={`Contact ${uiPrefs.columnVisibilityModel.contact_person ? 'On' : 'Off'}`}
                  color={uiPrefs.columnVisibilityModel.contact_person ? 'primary' : 'default'}
                  variant={uiPrefs.columnVisibilityModel.contact_person ? 'filled' : 'outlined'}
                  onClick={() => toggleColumnVisibility('contact_person')}
                />
                <Chip
                  clickable
                  label={`Units ${uiPrefs.columnVisibilityModel.business_units ? 'On' : 'Off'}`}
                  color={uiPrefs.columnVisibilityModel.business_units ? 'primary' : 'default'}
                  variant={uiPrefs.columnVisibilityModel.business_units ? 'filled' : 'outlined'}
                  onClick={() => toggleColumnVisibility('business_units')}
                />
                <Chip
                  clickable
                  label={`Created ${uiPrefs.columnVisibilityModel.created_at ? 'On' : 'Off'}`}
                  color={uiPrefs.columnVisibilityModel.created_at ? 'primary' : 'default'}
                  variant={uiPrefs.columnVisibilityModel.created_at ? 'filled' : 'outlined'}
                  onClick={() => toggleColumnVisibility('created_at')}
                />
              </Stack>
            )}
          </Stack>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {paginatedProjects.length === 0 ? (
          <Box
            sx={{
              py: 6,
              px: 2,
              textAlign: 'center',
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 2
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {hasActiveFilters ? 'No projects match your filters' : 'No projects yet'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {hasActiveFilters
                ? 'Try clearing filters or adjusting the search text.'
                : 'Create your first project to start organizing clients, scope and follow-up.'}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
              {hasActiveFilters && (
                <Button variant="outlined" onClick={() => setFilters({ q: '', status: '' })}>
                  Clear filters
                </Button>
              )}
              <Button variant="contained" startIcon={<AddCircleIcon />} onClick={() => handleOpenDialog()}>
                New Project
              </Button>
            </Box>
          </Box>
        ) : isMobile ? (
          <>
            <Grid container spacing={2}>
              {paginatedProjects.map((project) => (
                <Grid item xs={12} key={project.id}>
                  <ProjectCard
                    project={project}
                    onView={(p) => navigate(`/projects/${p.id}`)}
                    onEdit={(p) => handleOpenDialog(p)}
                    onDelete={(p) => setDeleteConfirm(p)}
                  />
                </Grid>
              ))}
            </Grid>

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, value) => setPage(value)}
                  color="primary"
                  size="small"
                  siblingCount={0}
                  boundaryCount={1}
                />
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ height: 640, width: '100%' }}>
            <DataGrid
              rows={filtered}
              columns={columns}
              columnVisibilityModel={uiPrefs.columnVisibilityModel}
              onColumnVisibilityModelChange={(model) => updateUiPrefs({ columnVisibilityModel: model })}
              density="compact"
              rowHeight={42}
              columnHeaderHeight={44}
              paginationModel={desktopPaginationModel}
              onPaginationModelChange={(model) => {
                setDesktopPaginationModel(model);
                if (model.pageSize !== uiPrefs.tablePageSize) {
                  updateUiPrefs({ tablePageSize: model.pageSize });
                }
              }}
              pageSizeOptions={[25, 50, 100]}
              disableRowSelectionOnClick
              disableColumnResize={false}
              sx={{
                border: 0,
                '& .MuiDataGrid-columnHeaders': {
                  bgcolor: alpha('#1976d2', 0.05),
                  borderRadius: 1,
                  fontWeight: 600,
                },
                '& .MuiDataGrid-cell': {
                  py: 0.4,
                  alignItems: 'center'
                },
                '& .MuiDataGrid-row:hover': { bgcolor: alpha('#1976d2', 0.03) },
                '& .MuiDataGrid-footerContainer': {
                  borderTop: '2px solid',
                  borderColor: 'divider',
                  bgcolor: alpha('#1976d2', 0.02),
                },
              }}
            />
          </Box>
        )}
      </Paper>

      {/* CREATE/EDIT DIALOG - SEPARATE COMPONENT */}
      <ProjectFormDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        editingProject={editingProject}
        error={error}
        formData={formData}
        onSave={handleSaveProject}
      />

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the project{' '}
            <strong>{deleteConfirm?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone and all associated information will be lost.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, '& .MuiButton-root': { flex: { xs: 1, sm: 'initial' } } }}>
          <Button onClick={() => setDeleteConfirm(null)} sx={{ fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={() => handleDeleteProject(deleteConfirm?.id)}
            variant="contained"
            color="error"
            sx={{ fontWeight: 600 }}
          >
            Delete Project
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
